import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';

// Interface for component props
interface DateTimePickerProps {
	initialValue: string;
	onChange: (dateTime: string) => void;
	label?: string;
}

const MilitaryDatePicker: React.FC<DateTimePickerProps> = ({
	initialValue = '',
	onChange,
	label
}) => {
	// Initialize state with blank values or provided initial value
	const initializeState = () => {
		if (initialValue) {
			const normalizedValue = initialValue.replace('T', ' ');
			const [datePart, timePart] = normalizedValue.split(' ');
			const [year, month, day] = datePart.split('-').map(Number);
			const [hour, minute] = (timePart || '').split(':').map(Number);
			console.log({ normalizedValue }, { year, month, day, hour, minute });
			return { year, month, day, hour, minute };
		} else {
			return {
				year: null as number | null,
				month: null as number | null,
				day: null as number | null,
				hour: null as number | null,
				minute: null as number | null,
			};
		}
	};

	const [selectedDate, setSelectedDate] = useState(initializeState);

	useEffect(() => {
		setSelectedDate(initializeState());
	}, [initialValue]);

	// Format the output string
	const formatDateTime = useCallback(() => {
		const { year, month, day, hour, minute } = selectedDate;

		// Only format if all values are selected
		if (year && month && day && hour !== null && minute !== null) {
			const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
			const formattedTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
			return `${formattedDate} ${formattedTime}`;
		}

		return 'Please select date and time';
	}, [selectedDate]);

	// Handle date/time changes
	const handleChange = (field: keyof typeof selectedDate, value: number) => {
		const newSelectedDate = { ...selectedDate, [field]: value };

		// Adjust day if month/year change makes current day invalid
		if ((field === 'month' || field === 'year') && newSelectedDate.month && newSelectedDate.year) {
			const daysInNewMonth = new Date(newSelectedDate.year, newSelectedDate.month, 0).getDate();
			if (newSelectedDate.day && newSelectedDate.day > daysInNewMonth) {
				newSelectedDate.day = daysInNewMonth;
			}
		}

		setSelectedDate(newSelectedDate);

		// Call onChange callback if provided and all fields are selected
		if (onChange && newSelectedDate.year && newSelectedDate.month && newSelectedDate.day &&
			newSelectedDate.hour !== null && newSelectedDate.minute !== null) {
			const formattedDateTime = `${newSelectedDate.year}-${newSelectedDate.month.toString().padStart(2, '0')}-${newSelectedDate.day.toString().padStart(2, '0')} ${newSelectedDate.hour.toString().padStart(2, '0')}:${newSelectedDate.minute.toString().padStart(2, '0')}`;
			onChange(formattedDateTime);
		}
	};

	const years = generateYears();
	const days = selectedDate.month && selectedDate.year ? generateDays(selectedDate.month, selectedDate.year) : [];
	const hours = generateHours();
	const minutes = generateMinutes();

	return (
		<PickerContainer>
			<div>
				<SectionTitle>{label || 'Select Date'}</SectionTitle>
				<DateSection>
					<div>
						<Label>Month:</Label>
						<Select
							value={selectedDate.month || ''}
							onChange={(e) => handleChange('month', parseInt(e.target.value))}
						>
							<option value="">Select Month</option>
							{months.map((month) => (
								<option key={month.value} value={month.value}>
									{month.label}
								</option>
							))}
						</Select>
					</div>

					<div>
						<Label>Day:</Label>
						<Select
							value={selectedDate.day || ''}
							onChange={(e) => handleChange('day', parseInt(e.target.value))}
							disabled={!selectedDate.month || !selectedDate.year}
						>
							<option value="">Select Day</option>
							{days.map((day) => (
								<option key={day} value={day}>
									{day}
								</option>
							))}
						</Select>
					</div>

					<div>
						<Label>Year:</Label>
						<Select
							value={selectedDate.year || ''}
							onChange={(e) => handleChange('year', parseInt(e.target.value))}
						>
							<option value="">Select Year</option>
							{years.map((year) => (
								<option key={year} value={year}>
									{year}
								</option>
							))}
						</Select>
					</div>
				</DateSection>
			</div>

			<div>
				<SectionTitle>Select Time (24-hour format)</SectionTitle>
				<TimeSection>
					<div>
						<Label>Hour:</Label>
						<Select
							value={selectedDate.hour !== null ? selectedDate.hour.toString().padStart(2, '0') : ''}
							onChange={(e) => handleChange('hour', parseInt(e.target.value))}
						>
							<option value="">Select Hour</option>
							{hours.map((hour) => (
								<option key={hour} value={hour}>
									{hour}
								</option>
							))}
						</Select>
					</div>

					<div>
						<Label>Min:</Label>
						<Select
							value={selectedDate.minute !== null ? selectedDate.minute.toString().padStart(2, '0') : ''}
							onChange={(e) => handleChange('minute', parseInt(e.target.value))}
						>
							<option value="">Select Min</option>
							{minutes.map((minute) => (
								<option key={minute} value={minute}>
									{minute}
								</option>
							))}
						</Select>
					</div>
				</TimeSection>
			</div>

			{/* <div>
				<SectionTitle>Selected DateTime</SectionTitle>
				<Output>{formatDateTime()}</Output>
			</div> */}
		</PickerContainer>
	);
};

export default MilitaryDatePicker;

// Generate arrays for dropdowns
const months = [
	{ value: 1, label: 'January' },
	{ value: 2, label: 'February' },
	{ value: 3, label: 'March' },
	{ value: 4, label: 'April' },
	{ value: 5, label: 'May' },
	{ value: 6, label: 'June' },
	{ value: 7, label: 'July' },
	{ value: 8, label: 'August' },
	{ value: 9, label: 'September' },
	{ value: 10, label: 'October' },
	{ value: 11, label: 'November' },
	{ value: 12, label: 'December' },
];

const generateYears = () => {
	const currentYear = new Date().getFullYear();
	const years = [];
	for (let i = currentYear - 1; i <= currentYear + 10; i++) { // Changed from currentYear - 10
		years.push(i);
	}
	return years;
};

const generateDays = (month: number, year: number) => {
	const daysInMonth = new Date(year, month, 0).getDate();
	const days = [];
	for (let i = 1; i <= daysInMonth; i++) {
		days.push(i);
	}
	return days;
};

const generateHours = () => {
	const hours = [];
	for (let i = 0; i < 24; i++) {
		hours.push(i.toString().padStart(2, '0'));
	}
	return hours;
};

const generateMinutes = () => {
	const minutes = [];
	for (let i = 0; i < 60; i++) { // Changed from i += 5 to i++
		minutes.push(i.toString().padStart(2, '0'));
	}
	return minutes;
};


// Styled components
const PickerContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 20px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	background-color: #ffffff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	max-width: 400px;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const SectionTitle = styled.h3`
	margin: 0 0 8px 0;
	font-size: 14px;
	font-weight: 600;
	color: #333;
`;

const DateSection = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;

const TimeSection = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;

const Select = styled.select`
	padding: 8px 12px;
	border: 1px solid #ccc;
	border-radius: 4px;
	background-color: white;
	font-size: 14px;
	cursor: pointer;
	
	&:focus {
		outline: none;
		border-color: #007bff;
		box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
	}
	
	&:disabled {
		background-color: #f5f5f5;
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const Output = styled.div`
	padding: 12px;
	background-color: #f8f9fa;
	border: 1px solid #e9ecef;
	border-radius: 4px;
	font-family: 'Courier New', monospace;
	font-size: 16px;
	font-weight: bold;
	color: #495057;
`;

const Label = styled.label`
	font-size: 12px;
	color: #666;
	margin-right: 4px;
	min-width: 40px;
`;